/** GCS resumable upload chunk size (must be a multiple of 256 KiB except the final chunk). */
const CHUNK_SIZE_BYTES = 8 * 1024 * 1024;
const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 1000;
const CHUNK_STALL_TIMEOUT_MS = 60 * 1000;
const MAX_STALL_RECOVERIES = 3;

const isRetryableStatus = (status) => status >= 500 || status === 408 || status === 429;

const errorWithBody = async (prefix, response) => {
  const body = await response.text().catch(() => '');
  const err = new Error(
    `${prefix} (${response.status})${body ? `: ${body.slice(0, 200)}` : ''}`
  );
  err.status = response.status;
  return err;
};

const createStallError = (message) => {
  const err = new Error(message);
  err.code = 'UPLOAD_STALLED';
  return err;
};

/**
 * Queries GCS for the last confirmed byte so we can resume after a failed chunk.
 * Returns the next offset to upload from (0 if nothing has been received yet).
 */
const queryResumeOffset = async (uploadUrl, total) => {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Range': `bytes */${total}`,
      'Content-Length': '0'
    }
  });

  // 308 means upload is still in progress — Range header tells us what GCS has
  if (response.status === 308) {
    const range = response.headers.get('Range');
    if (!range) return 0;
    const match = range.match(/bytes=0-(\d+)/);
    return match ? parseInt(match[1], 10) + 1 : 0;
  }

  // 200/201 means GCS already considers the upload complete
  if (response.status === 200 || response.status === 201) {
    return total;
  }

  throw await errorWithBody('Failed to query resume offset', response);
};

/**
 * Uploads a single chunk with retries, exponential backoff, and a stall timeout.
 * Retries network failures, stalls, and transient HTTP statuses; fails fast on permanent 4xx.
 */
const uploadChunkWithRetry = async (uploadUrl, chunk, offset, total, contentType) => {
  const end = offset + chunk.size - 1;
  let attempt = 0;

  while (attempt < MAX_RETRIES) {
    const controller = new AbortController();
    const stallTimer = setTimeout(() => {
      controller.abort();
    }, CHUNK_STALL_TIMEOUT_MS);

    try {
      const response = await fetch(uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': contentType,
          'Content-Range': `bytes ${offset}-${end}/${total}`
        },
        body: chunk,
        signal: controller.signal
      });

      clearTimeout(stallTimer);

      const isLast = end === total - 1;

      if (isLast) {
        if (response.status === 200 || response.status === 201) return response;
        throw await errorWithBody('GCS upload failed', response);
      }

      // 308 Resume Incomplete is expected for non-final chunks.
      if (response.status === 308) {
        return response;
      }

      if (response.status === 200 || response.status === 201) {
        return { resumeFrom: total };
      }

      throw await errorWithBody('GCS chunk upload failed', response);
    } catch (err) {
      clearTimeout(stallTimer);

      const isAbort = err?.name === 'AbortError';
      const status = err.status ?? null;

      if (status && !isRetryableStatus(status) && !isAbort) {
        throw err;
      }

      attempt++;
      if (attempt >= MAX_RETRIES) {
        if (isAbort) {
          throw createStallError(
            'Upload stalled. Please check your connection and try again.'
          );
        }
        throw err;
      }

      await new Promise((r) => setTimeout(r, RETRY_DELAY_MS * attempt));
    }
  }
};

/**
 * Uploads a file to a GCS resumable session URL with chunked PUTs.
 * Do not attach Authorization; the session URL is the credential.
 */
export const uploadFileToGcsResumable = async (uploadUrl, file, contentType, onProgress) => {
  if (!uploadUrl) throw new Error('upload_url is required');
  if (!(file instanceof Blob)) throw new Error('file is required');

  const total = file.size;
  if (total <= 0) throw new Error('file is empty');

  const mimeType = contentType || file.type || 'application/octet-stream';
  let offset = 0;
  let stallRecoveries = 0;
  let lastConfirmedOffset = 0;

  const reportProgress = (value, meta) => {
    if (typeof onProgress === 'function') {
      onProgress(Math.min(1, Math.max(0, value)), meta);
    }
  };

  reportProgress(0);

  while (offset < total) {
    const end = Math.min(offset + CHUNK_SIZE_BYTES, total);
    const chunk = file.slice(offset, end);

    try {
      const result = await uploadChunkWithRetry(uploadUrl, chunk, offset, total, mimeType);

      if (result?.resumeFrom !== undefined) {
        offset = result.resumeFrom;
      } else {
        offset = end;
      }

      if (offset > lastConfirmedOffset) {
        lastConfirmedOffset = offset;
        stallRecoveries = 0;
      }

      reportProgress(offset / total);
    } catch (err) {
      const canResume =
        err?.code === 'UPLOAD_STALLED' ||
        err?.name === 'TypeError' ||
        err?.name === 'AbortError' ||
        isRetryableStatus(err?.status);

      if (!canResume) {
        throw err;
      }

      stallRecoveries++;
      if (stallRecoveries > MAX_STALL_RECOVERIES) {
        throw createStallError(
          'Upload stalled. Please check your connection and try again.'
        );
      }

      reportProgress(offset / total, { stalled: true });

      const resumedOffset = await queryResumeOffset(uploadUrl, total);
      if (resumedOffset > lastConfirmedOffset) {
        lastConfirmedOffset = resumedOffset;
        stallRecoveries = 0;
      }

      offset = resumedOffset;
      reportProgress(offset / total);

      if (offset >= total) {
        return;
      }
    }
  }
};
